import type { ConversationKind } from './enums';
import {
  READ_RECEIPTS_MAX_CHANNEL_MEMBERS,
  type ReadReceiptsDisabledReason,
  type ReadReceiptsSetting,
} from './enums/notifications';

/**
 * Read receipts (D-550, SPEC §4.3): which conversations show them. One pure rule used by the API
 * (`GET /conversations/:id/read-receipts`, the `receipt.updated` publisher), the web and the
 * mobile app, so a client never asks for receipts the server would refuse.
 *
 * - `off`: nowhere. `dms`: DMs and group DMs. `all`: DMs, group DMs and channels with at most
 *   {@link READ_RECEIPTS_MAX_CHANNEL_MEMBERS} active members.
 * - Self DMs and task conversations never show receipts.
 * - Reciprocity: a person who turned `shareReadReceipts` off neither shares their read position
 *   nor sees anyone else's (`not_sharing`).
 */

export interface ReadReceiptsCoverageInput {
  setting: ReadReceiptsSetting;
  kind: ConversationKind;
  /** Active members (`conversations.member_count`). */
  memberCount: number;
}

export type ReadReceiptsCoverage =
  | { covered: true }
  | { covered: false; reason: Exclude<ReadReceiptsDisabledReason, 'not_sharing'> };

export function readReceiptsCoverage(input: ReadReceiptsCoverageInput): ReadReceiptsCoverage {
  if (input.setting === 'off') return { covered: false, reason: 'workspace_off' };
  switch (input.kind) {
    case 'dm':
    case 'group_dm':
      return { covered: true };
    case 'public_channel':
    case 'private_channel':
      if (input.setting !== 'all') return { covered: false, reason: 'conversation_kind' };
      return input.memberCount > READ_RECEIPTS_MAX_CHANNEL_MEMBERS
        ? { covered: false, reason: 'too_many_members' }
        : { covered: true };
    default:
      return { covered: false, reason: 'conversation_kind' };
  }
}

/**
 * Whether `viewer` sees receipts in a conversation: covered by the setting and the viewer shares
 * their own. Returns the reason when not (the API's `disabled` field).
 */
export function readReceiptsVisibility(
  input: ReadReceiptsCoverageInput & { viewerShares: boolean },
): { visible: true } | { visible: false; reason: ReadReceiptsDisabledReason } {
  const coverage = readReceiptsCoverage(input);
  if (!coverage.covered) return { visible: false, reason: coverage.reason };
  if (!input.viewerShares) return { visible: false, reason: 'not_sharing' };
  return { visible: true };
}
